import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import allQuestions from '../data/questionsLoader';
import { loadDocumentsAsync } from '../data/documentsLoader';

export interface Question {
  id: string;
  category: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
}

export interface UserStats {
  totalAnswered: number;
  correctAnswers: number;
  incorrectAnswers: number;
  categoryStats: Record<string, { correct: number; incorrect: number; answeredQuestions: string[] }>;
  questionErrors: Record<string, number>;
  studyTimeMinutes: number;
  examTimeMinutes: number;
  marathonTimeMinutes: number;
}

export interface Settings {
  theme: 'light' | 'dark';
  backgroundGradient: 'default' | 'gradient-1' | 'gradient-2' | 'gradient-3';
  questionFontSize: number; // Percentage from 0-100
  answerFontSize: number; // Percentage from 0-100
  documentFontSize: number; // Percentage from 0-100
  fontFamily: string;
}

export interface TestSession {
  id: string;
  type: 'preparation' | 'exam' | 'marathon';
  questions: Question[];
  currentQuestionIndex: number;
  answers: number[];
  startTime: number;
  category?: string;
  marathonId?: string;
  isCompleted?: boolean;
  finalAnswer?: number; // Final answer to ensure it's captured
}

export interface Marathon {
  id: string;
  name: string;
  categories: string[];
  includeFavorites: boolean;
  includeIncorrect: boolean;
  completedQuestions: number;
  totalQuestions: number;
  sessionCorrectAnswers?: number; // Количество правильных ответов в текущей сессии марафона
  questionOrder?: string[]; // Порядок ID вопросов для сохранения последовательности
  randomOrder?: boolean; // Включен ли случайный порядок вопросов
}

export interface DocumentBookmark {
  id: string;
  documentId: string;
  position: number;
  text: string;
  title: string;
  timestamp: number;
}

export interface DocumentReadingProgress {
  documentId: string;
  position: number;
  timestamp: number;
}

export interface Document {
  id: string;
  title: string;
  content: string;
  category: string;
  isBookmarked?: boolean;
}

interface AppContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  resetSettings: () => void;
  getBackgroundClass: () => string;
  userStats: UserStats;
  updateStats: (questionId: string, correct: boolean, category: string) => void;
  resetQuestionErrors: (questionId: string) => void;
  resetAllStats: () => void;
  clearAllStorageData: () => void;
  loadTestData: () => void;
  updateExamTime: (additionalMinutes: number) => void;
  updateMarathonTime: (additionalMinutes: number) => void;
  favorites: Set<string>;
  toggleFavorite: (questionId: string) => void;
  testSession: TestSession | null;
  startTest: (type: 'preparation' | 'exam' | 'marathon', questions: Question[], category?: string, marathonId?: string, startIndex?: number) => void;
  updateTestSession: (updates: Partial<TestSession>) => void;
  completeTest: () => void;
  endTest: () => void;
  updateStudyTime: (additionalMinutes: number) => void;
  marathons: Marathon[];
  addMarathon: (marathon: Marathon) => void;
  updateMarathon: (id: string, updates: Partial<Marathon>) => void;
  deleteMarathon: (id: string) => void;
  questions: Question[];
  categories: string[];
  documents: Document[];
  documentBookmarks: Set<string>;
  toggleDocumentBookmark: (documentId: string) => void;
  selectedDocument: Document | null;
  setSelectedDocument: (document: Document | null) => void;
  selectedBookmark: DocumentBookmark | null;
  setSelectedBookmark: (bookmark: DocumentBookmark | null) => void;
  documentInternalBookmarks: DocumentBookmark[];
  addDocumentInternalBookmark: (bookmark: DocumentBookmark) => void;
  removeDocumentInternalBookmark: (bookmarkId: string) => void;
  getDocumentInternalBookmarks: (documentId: string) => DocumentBookmark[];
  getAllDocumentBookmarks: () => DocumentBookmark[];
  jumpToBookmark: (bookmark: DocumentBookmark) => void;
  removeBookmarkById: (bookmarkId: string) => void;
  documentReadingProgress: Record<string, DocumentReadingProgress>;
  updateReadingProgress: (documentId: string, position: number) => void;
  getReadingProgress: (documentId: string) => DocumentReadingProgress | undefined;
  searchQuestions: (query: string, selectedCategories: string[]) => Question[];
}

const AppContext = createContext<AppContextType | null>(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Mock documents data
const mockDocuments: Document[] = [
  {
    id: 'doc1',
    title: 'Основы гуманитарных наук',
    category: 'История',
    content: `# Основы гуманитарных наук

## Введение
Гуманитарные науки изучают человека и его деятельность в обществе.

## История
История изучает прошлое человечества на основе письменных источников.

## География
География изучает природные и общественные явления на поверхности Земли.

## Литература
Литература - вид искусства, воплощающий действительность в художественных образах.`
  }
];

const createEmptyData = () => ({
  userStats: {
    totalAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    categoryStats: {},
    questionErrors: {},
    studyTimeMinutes: 0,
    examTimeMinutes: 0,
    marathonTimeMinutes: 0
  },
  favorites: new Set<string>(),
  marathons: [],
  documentBookmarks: new Set<string>(),
  documentInternalBookmarks: [],
  documentReadingProgress: {}
});

// Тестовые данные отдельно для демонстрации
const createTestData = () => ({
  userStats: {
    totalAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    categoryStats: {},
    questionErrors: {
      'h1': 2, // Вопрос о Второй мировой войне - 2 ошибки
      'h7': 1, // Вопрос о столице Австралии - 1 ошибка  
      'h13': 3 // Вопрос о "Преступлении и наказании" - 3 ошибки
    },
    studyTimeMinutes: 0,
    examTimeMinutes: 0,
    marathonTimeMinutes: 0
  },
  favorites: new Set<string>([
    'h2', // Первый президент России
    'h8', // Самый большой океан
    'h14', // Автор "Войны и мира"
    'h19' // "Я мыслю, следовательно, существую"
  ]),
  marathons: [],
  documentBookmarks: new Set<string>(),
  documentInternalBookmarks: [],
  documentReadingProgress: {}
});

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>({
    theme: 'light',
    backgroundGradient: 'gradient-1', // Ровно - по умолчанию
    questionFontSize: 50, // 50% - equivalent to ~21px
    answerFontSize: 50, // 50% - equivalent to ~18px
    documentFontSize: 50, // 50% - equivalent to ~16px
    fontFamily: 'system-ui'
  });

  // Единое хранилище данных без специализаций
  const [appData, setAppData] = useState(() => createEmptyData());
  const [testSession, setTestSession] = useState<TestSession | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedBookmark, setSelectedBookmark] = useState<DocumentBookmark | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const isFirstRender = useRef(true);

  const questions = useMemo(() => allQuestions, []);
  const categories = useMemo(() => {
    return Array.from(new Set(questions.map(q => q.category)));
  }, [questions]);

  // Документы загружаются асинхронно через dynamic import — не блокируют начальный рендер
  const [documents, setDocuments] = useState<Document[]>([]);
  useEffect(() => {
    loadDocumentsAsync().then(setDocuments).catch(console.error);
  }, []);

  // Load data from localStorage on mount
  useEffect(() => {
    try {
      // Load main data
      const savedData = localStorage.getItem('testApp-data');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        setAppData({
          ...parsed,
          favorites: new Set(parsed.favorites || []),
          documentBookmarks: new Set(parsed.documentBookmarks || []),
          documentInternalBookmarks: parsed.documentInternalBookmarks || [],
          documentReadingProgress: parsed.documentReadingProgress || {}
        });
      } else {
        // Если нет сохраненных данных, используем пустые данные
        setAppData(createEmptyData());
      }
      
      // Load interrupted test session
      const savedTestSession = localStorage.getItem('testApp-testSession');
      console.log('📂 Looking for saved test session...', savedTestSession ? 'Found' : 'Not found');
      if (savedTestSession) {
        const parsed = JSON.parse(savedTestSession);
        console.log('📋 Parsed test session:', {
          type: parsed.type,
          isCompleted: parsed.isCompleted,
          currentQuestionIndex: parsed.currentQuestionIndex,
          questionsCount: parsed.questions?.length
        });
        // Restore test session if it's not completed
        if (parsed && !parsed.isCompleted) {
          setTestSession(parsed);
          console.log('✅ Restored interrupted test session:', parsed.type, 'at question', parsed.currentQuestionIndex + 1);
        } else {
          console.log('⏭️ Test session was completed, not restoring');
        }
      }
      
      // Mark as initialized after loading everything
      setIsInitialized(true);
      console.log('🎬 App initialization complete');
    } catch (error) {
      console.error('Error loading data:', error);
      // При ошибке загрузки используем пустые данные
      setAppData(createEmptyData());
      setIsInitialized(true);
    }
  }, []);

  // Save data to localStorage
  useEffect(() => {
    try {
      const dataToSave = {
        ...appData,
        favorites: Array.from(appData.favorites),
        documentBookmarks: Array.from(appData.documentBookmarks)
      };
      localStorage.setItem('testApp-data', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving data:', error);
    }
  }, [appData]);
  
  // Save test session to localStorage
  useEffect(() => {
    // Skip on first render - let data load first
    if (isFirstRender.current) {
      console.log('⏸️ Skipping test session save - first render');
      isFirstRender.current = false;
      return;
    }
    
    // Skip saving on initial render before data is loaded
    if (!isInitialized) {
      console.log('⏸️ Skipping test session save - not initialized yet');
      return;
    }
    
    try {
      if (testSession && !testSession.isCompleted) {
        // Save only active (not completed) test sessions
        localStorage.setItem('testApp-testSession', JSON.stringify(testSession));
        console.log('💾 Saved test session:', testSession.type, 'question', testSession.currentQuestionIndex + 1);
      } else if (testSession === null) {
        // Clear saved session when test is completed or cleared
        localStorage.removeItem('testApp-testSession');
        console.log('🗑️ Cleared saved test session (user ended test)');
      } else if (testSession && testSession.isCompleted) {
        // Test is completed - remove from localStorage so it doesn't restore
        localStorage.removeItem('testApp-testSession');
        console.log('✅ Test completed - removed from storage');
      }
    } catch (error) {
      console.error('Error saving test session:', error);
    }
  }, [testSession, isInitialized]);
  
  // Save test session on page unload (browser close, tab close, navigation away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        if (testSession && !testSession.isCompleted) {
          localStorage.setItem('testApp-testSession', JSON.stringify(testSession));
          console.log('💾 Saved test session on unload');
        }
      } catch (error) {
        console.error('Error saving test session on unload:', error);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload); // For iOS Safari
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [testSession]);

  // Apply theme to document element
  useEffect(() => {
    const root = document.documentElement;
    if (settings.theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [settings.theme]);

  // Apply font size settings to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    // Convert percentage to actual font size
    const baseQuestionSize = 21; // Base size for 50%
    const baseAnswerSize = 18; // Base size for 50%
    const baseDocumentSize = 16; // Base size for 50%
    
    const questionSize = Math.round((settings.questionFontSize / 50) * baseQuestionSize);
    const answerSize = Math.round((settings.answerFontSize / 50) * baseAnswerSize);
    const documentSize = Math.round((settings.documentFontSize / 50) * baseDocumentSize);
    
    root.style.setProperty('--question-font-size', `${questionSize}px`);
    root.style.setProperty('--answer-font-size', `${answerSize}px`);
    root.style.setProperty('--document-font-size', `${documentSize}px`);
  }, [settings.questionFontSize, settings.answerFontSize, settings.documentFontSize]);

  // Load and save settings to localStorage
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('testApp-settings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsedSettings }));
      } else {
        // If no saved settings, use defaults
        setSettings(prev => ({ 
          ...prev, 
          theme: 'light',
          backgroundGradient: 'gradient-1' // Ровно - по умолчанию
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('testApp-settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }, [settings]);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const resetSettings = () => {
    const defaultSettings: Settings = {
      theme: 'light',
      backgroundGradient: 'gradient-1', // Ровно - по умолчанию
      questionFontSize: 50, // 50% - equivalent to ~21px
      answerFontSize: 50, // 50% - equivalent to ~18px
      documentFontSize: 50, // 50% - equivalent to ~16px
      fontFamily: 'system-ui'
    };
    setSettings(defaultSettings);
  };

  const updateAppData = useCallback((updates: Partial<typeof appData>) => {
    setAppData(prev => ({ ...prev, ...updates }));
  }, []);

  const updateStats = useCallback((questionId: string, correct: boolean, category: string) => {
    setAppData(prevData => {
      // Get current category stats
      const currentCategoryStats = prevData.userStats.categoryStats[category] || {
        correct: 0,
        incorrect: 0,
        answeredQuestions: []
      };
      
      // Update category stats (always increment for each answer)
      const updatedCategoryStats = {
        ...currentCategoryStats,
        correct: correct ? currentCategoryStats.correct + 1 : currentCategoryStats.correct,
        incorrect: !correct ? currentCategoryStats.incorrect + 1 : currentCategoryStats.incorrect,
        answeredQuestions: currentCategoryStats.answeredQuestions.includes(questionId) 
          ? currentCategoryStats.answeredQuestions 
          : [...currentCategoryStats.answeredQuestions, questionId]
      };
      
      // Update question errors (only increment for incorrect answers)
      const currentErrors = prevData.userStats.questionErrors[questionId] || 0;
      const updatedQuestionErrors = {
        ...prevData.userStats.questionErrors,
        [questionId]: !correct ? currentErrors + 1 : currentErrors
      };
      
      return {
        ...prevData,
        userStats: {
          ...prevData.userStats,
          totalAnswered: prevData.userStats.totalAnswered + 1,
          correctAnswers: correct ? prevData.userStats.correctAnswers + 1 : prevData.userStats.correctAnswers,
          incorrectAnswers: correct ? prevData.userStats.incorrectAnswers : prevData.userStats.incorrectAnswers + 1,
          categoryStats: {
            ...prevData.userStats.categoryStats,
            [category]: updatedCategoryStats
          },
          questionErrors: updatedQuestionErrors
        }
      };
    });
  }, []);

  const resetQuestionErrors = useCallback((questionId: string) => {
    setAppData(prev => ({
      ...prev,
      userStats: {
        ...prev.userStats,
        questionErrors: {
          ...prev.userStats.questionErrors,
          [questionId]: 0
        }
      }
    }));
  }, []);

  const resetAllStats = useCallback(() => {
    setAppData(prev => ({
      ...prev,
      userStats: {
        totalAnswered: 0,
        correctAnswers: 0,
        incorrectAnswers: 0,
        categoryStats: {},
        questionErrors: {},
        studyTimeMinutes: 0,
        examTimeMinutes: 0,
        marathonTimeMinutes: 0
      }
    }));
  }, []);

  // Debug function to clear all localStorage data 
  const clearAllStorageData = () => {
    try {
      // Полная очистка localStorage
      localStorage.removeItem('testApp-data');
      localStorage.removeItem('testApp-settings');
      
      // Также очищаем все возможные старые ключи
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('test-app') || key.startsWith('testApp'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Устанавливаем полностью пустые данные
      const emptyData = createEmptyData();
      setAppData(emptyData);
      
      console.log('All localStorage data cleared completely');
      console.log('Empty data set:', {
        favorites: emptyData.favorites.size,
        questionErrors: Object.keys(emptyData.userStats.questionErrors).length
      });
      
      // Перезагружаем страницу для полного сброса
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  // Функция для загрузки тестовых данных
  const loadTestData = () => {
    try {
      const testData = createTestData();
      setAppData(testData);
      console.log('Test data loaded');
      console.log('Test favorites:', Array.from(testData.favorites));
      console.log('Test questionErrors:', testData.userStats.questionErrors);
    } catch (error) {
      console.error('Error loading test data:', error);
    }
  };

  const toggleFavorite = useCallback((questionId: string) => {
    setAppData(prev => {
      const newFavorites = new Set(prev.favorites);
      if (newFavorites.has(questionId)) {
        newFavorites.delete(questionId);
      } else {
        newFavorites.add(questionId);
      }
      
      return {
        ...prev,
        favorites: newFavorites
      };
    });
  }, []);

  const startTest = (type: 'preparation' | 'exam' | 'marathon', testQuestions: Question[], category?: string, marathonId?: string, startIndex?: number) => {
    console.log('AppContext.startTest called:', {
      type,
      questionsCount: testQuestions.length,
      category,
      marathonId,
      startIndex
    });
    
    // Проверка структуры первого вопроса
    if (testQuestions.length > 0) {
      console.log('First question structure:', {
        hasId: !!testQuestions[0]?.id,
        hasQuestion: !!testQuestions[0]?.question,
        hasOptions: !!testQuestions[0]?.options,
        hasCorrectAnswer: testQuestions[0]?.correctAnswer !== undefined,
        firstQuestion: testQuestions[0]
      });
    }
    
    const session: TestSession = {
      id: Math.random().toString(36).substr(2, 9),
      type,
      questions: testQuestions,
      currentQuestionIndex: startIndex || 0,
      answers: [],
      startTime: Date.now(),
      category,
      marathonId
    };
    
    console.log('TestSession created:', {
      id: session.id,
      questionsLength: session.questions.length,
      currentQuestionIndex: session.currentQuestionIndex,
      firstQuestionExists: !!session.questions[0]
    });
    
    setTestSession(session);
  };

  const updateTestSession = (updates: Partial<TestSession>) => {
    if (testSession) {
      setTestSession({ ...testSession, ...updates });
    }
  };

  const updateStudyTime = useCallback((additionalMinutes: number) => {
    setAppData(prev => ({
      ...prev,
      userStats: {
        ...prev.userStats,
        studyTimeMinutes: prev.userStats.studyTimeMinutes + additionalMinutes
      }
    }));
  }, []);

  const updateExamTime = useCallback((additionalMinutes: number) => {
    setAppData(prev => ({
      ...prev,
      userStats: {
        ...prev.userStats,
        examTimeMinutes: prev.userStats.examTimeMinutes + additionalMinutes
      }
    }));
  }, []);

  const updateMarathonTime = useCallback((additionalMinutes: number) => {
    setAppData(prev => ({
      ...prev,
      userStats: {
        ...prev.userStats,
        marathonTimeMinutes: prev.userStats.marathonTimeMinutes + additionalMinutes
      }
    }));
  }, []);

  const completeTest = () => {
    if (testSession) {
      // Обновляем время в зависимости от типа теста
      const timeSpentMs = Date.now() - testSession.startTime;
      const timeSpentMinutes = Math.ceil(timeSpentMs / (1000 * 60)); // Округляем вверх до минут
      
      if (testSession.type === 'preparation') {
        updateStudyTime(timeSpentMinutes);
      } else if (testSession.type === 'exam') {
        updateExamTime(timeSpentMinutes);
      } else if (testSession.type === 'marathon') {
        updateMarathonTime(timeSpentMinutes);
      }
      
      // КРИТИЧЕСКИ ВАЖНО: Сохраняем ВСЕ поля, включая finalAnswer
      console.log('completeTest - перед обновлением testSession:', {
        sessionId: testSession.id,
        finalAnswer: testSession.finalAnswer,
        isCompleted: testSession.isCompleted,
        answers: testSession.answers
      });
      
      // Только устанавливаем isCompleted, если оно еще не установлено
      // Это предотвращает перезапись finalAnswer
      if (!testSession.isCompleted) {
        updateTestSession({ isCompleted: true });
      }
      
      console.log('completeTest - завершено');
    }
  };

  const endTest = () => {
    console.log('❌ endTest called - clearing test session');
    console.trace('Stack trace:');
    setTestSession(null);
  };

  const addMarathon = useCallback((marathon: Marathon) => {
    setAppData(prev => ({
      ...prev,
      marathons: [...prev.marathons, marathon]
    }));
  }, []);

  const updateMarathon = useCallback((id: string, updates: Partial<Marathon>) => {
    setAppData(prev => ({
      ...prev,
      marathons: prev.marathons.map(m => m.id === id ? { ...m, ...updates } : m)
    }));
  }, []);

  const deleteMarathon = useCallback((id: string) => {
    // Если есть активная testSession с этим marathonId, очищаем её
    if (testSession && testSession.marathonId === id) {
      setTestSession(null);
    }
    
    setAppData(prev => ({
      ...prev,
      marathons: prev.marathons.filter(m => m.id !== id)
    }));
  }, [testSession]);

  const toggleDocumentBookmark = useCallback((documentId: string) => {
    setAppData(prev => {
      const newBookmarks = new Set(prev.documentBookmarks);
      if (newBookmarks.has(documentId)) {
        newBookmarks.delete(documentId);
      } else {
        newBookmarks.add(documentId);
      }
      
      return {
        ...prev,
        documentBookmarks: newBookmarks
      };
    });
  }, []);

  const addDocumentInternalBookmark = useCallback((bookmark: DocumentBookmark) => {
    setAppData(prev => ({
      ...prev,
      documentInternalBookmarks: [...prev.documentInternalBookmarks, bookmark]
    }));
  }, []);

  const removeDocumentInternalBookmark = useCallback((bookmarkId: string) => {
    setAppData(prev => ({
      ...prev,
      documentInternalBookmarks: prev.documentInternalBookmarks.filter(b => b.id !== bookmarkId)
    }));
  }, []);

  const getDocumentInternalBookmarks = (documentId: string): DocumentBookmark[] => {
    return appData.documentInternalBookmarks
      .filter(bookmark => bookmark.documentId === documentId)
      .sort((a, b) => b.timestamp - a.timestamp);
  };

  const getAllDocumentBookmarks = (): DocumentBookmark[] => {
    return appData.documentInternalBookmarks.sort((a, b) => b.timestamp - a.timestamp);
  };

  const jumpToBookmark = (bookmark: DocumentBookmark) => {
    const doc = documents.find(d => d.id === bookmark.documentId);
    if (doc) {
      setSelectedDocument(doc);
      setSelectedBookmark(bookmark);
    }
  };

  const removeBookmarkById = (bookmarkId: string) => {
    removeDocumentInternalBookmark(bookmarkId);
  };

  const updateReadingProgress = useCallback((documentId: string, position: number) => {
    console.log('💾 AppContext.updateReadingProgress called:', { documentId, position });
    setAppData(prev => {
      const newProgress = {
        ...prev,
        documentReadingProgress: {
          ...prev.documentReadingProgress,
          [documentId]: {
            documentId,
            position,
            timestamp: Date.now()
          }
        }
      };
      console.log('💾 New progress state:', newProgress.documentReadingProgress[documentId]);
      return newProgress;
    });
  }, []);

  const getReadingProgress = (documentId: string): DocumentReadingProgress | undefined => {
    return appData.documentReadingProgress[documentId];
  };

  const searchQuestions = (query: string, selectedCategories: string[]): Question[] => {
    const lowercaseQuery = query.toLowerCase();
    
    return questions.filter(question => {
      // Filter by category if any are selected
      if (selectedCategories.length > 0 && !selectedCategories.includes(question.category)) {
        return false;
      }
      
      // Search in question text and options
      const matchesQuestion = question.question.toLowerCase().includes(lowercaseQuery);
      const matchesOptions = question.options.some(option => 
        option.toLowerCase().includes(lowercaseQuery)
      );
      const matchesExplanation = question.explanation?.toLowerCase().includes(lowercaseQuery) || false;
      
      return matchesQuestion || matchesOptions || matchesExplanation;
    });
  };

  const getBackgroundClass = () => {
    // Явно прописываем все классы, чтобы Tailwind их видел при сборке
    switch (settings.backgroundGradient) {
      case 'default':
        return 'glass-bg-default';
      case 'gradient-1':
        return 'glass-bg-gradient-1';
      case 'gradient-2':
        return 'glass-bg-gradient-2';
      case 'gradient-3':
        return 'glass-bg-gradient-3';
      default:
        return 'glass-bg-gradient-1';
    }
  };

  const value: AppContextType = {
    settings,
    updateSettings,
    resetSettings,
    getBackgroundClass,
    userStats: appData.userStats,
    updateStats,
    resetQuestionErrors,
    resetAllStats,
    clearAllStorageData,
    loadTestData,
    updateExamTime,
    updateMarathonTime,
    favorites: appData.favorites,
    toggleFavorite,
    testSession,
    startTest,
    updateTestSession,
    completeTest,
    endTest,
    updateStudyTime,
    marathons: appData.marathons,
    addMarathon,
    updateMarathon,
    deleteMarathon,
    questions,
    categories,
    documents,
    documentBookmarks: appData.documentBookmarks,
    toggleDocumentBookmark,
    selectedDocument,
    setSelectedDocument,
    selectedBookmark,
    setSelectedBookmark,
    documentInternalBookmarks: appData.documentInternalBookmarks,
    addDocumentInternalBookmark,
    removeDocumentInternalBookmark,
    getDocumentInternalBookmarks,
    getAllDocumentBookmarks,
    jumpToBookmark,
    removeBookmarkById,
    documentReadingProgress: appData.documentReadingProgress,
    updateReadingProgress,
    getReadingProgress,
    searchQuestions
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
